/**
 * File: adminVersionUtils.ts
 * Path: apps/admin/src/shared/utils/adminVersionUtils.ts
 * Description: Which build of the admin this page is running, and whether a
 * newer one has been deployed. The build id is stamped in by vite.config.ts
 * and the same id is published at /version.json, so comparing the two tells
 * an open tab it is out of date. Nothing here runs in dev or in tests.
 */

declare const __ADMIN_VERSION__: string | undefined;

/** The id of the build running now, or null in dev and tests. */
export const ADMIN_VERSION: string | null = typeof __ADMIN_VERSION__ === "string" && __ADMIN_VERSION__ ? __ADMIN_VERSION__ : null;

export const VERSION_FILE = "/version.json";

/** True when the deployed build is a different one from the one running. */
export const isNewerBuild = (running: string | null, deployed: unknown): boolean =>
  running !== null && typeof deployed === "string" && deployed !== "" && deployed !== running;

/** Asks the server which build is live. Null when it can't be reached (offline, a bad reply). */
export const fetchDeployedVersion = async (fetcher: typeof fetch = fetch): Promise<string | null> => {
  try {
    const response = await fetcher(`${VERSION_FILE}?t=${Date.now()}`, { cache: "no-store" });
    if (!response.ok) return null;
    const body: unknown = await response.json();
    const version = (body as { version?: unknown } | null)?.version;
    return typeof version === "string" ? version : null;
  } catch {
    return null;
  }
};
